import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  MatExpansionModule,
  MatExpansionPanel,
} from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { combineLatest, map, Observable } from 'rxjs';
import { PermissionService } from '../../shared/services/permission.service';
import { EmployeeService } from '../../features/employees/employee.service';
import { RolesService } from '../../features/roles/services/roles.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, MatExpansionModule, MatTooltipModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit {
  @Input() sidebarData: any;
  @Output() triggerSidebarAction = new EventEmitter<void>();
  @Input() toggleSidebar!: () => void;

  isCollapsed = false;
  mattabnumber = 0;
  currentPath = '';
  allowedPath = [
    '/dashboard',
    '/inventory',
    '/banking',
    '/branch',
    '/assets',
    '/reports',
    '/roles',
    '/configure',
  ];

  @ViewChildren(MatExpansionPanel)
  expansionPanels: QueryList<MatExpansionPanel>;

  profileName: any;
  loggedInUser: any;
  permissions: any;
  routePermissions: Record<string, Observable<boolean>> = {};
  filteredHrmsRoutes: string[] = [];
  filteredAccountsRoutes: string[] = [];
  filteredSalesRoutes: string[] = [];
  filteredServiceRoutes: string[] = [];
  filteredPurchaseRoutes: string[] = [];
  filteredAssetsRoutes: string[] = [];
  userModules: any[] = [];

  routeModuleMap: Record<string, number> = {
    '/employees': ModuleIds.Employee,
    '/attendance-management': ModuleIds.Attendance,
    '/leaves': ModuleIds.Leave,
    '/payroll': ModuleIds.Payroll,
    '/vacation-management': ModuleIds.Vacation,
    '/holiday': ModuleIds.Holiday,
    '/designations': ModuleIds.Designation,
    '/departments': ModuleIds.Departments,
    '/accommodation': ModuleIds.Accommodation,
    '/currency-adjustments': ModuleIds.Currency,
    '/manual-journals': ModuleIds.ManualJournal,
    '/chart-of-accounts': ModuleIds.ChartAccount,
    '/banking': ModuleIds.Banking,
    '/branch': ModuleIds.Branch,
    '/expenses': ModuleIds.Expenses,
    '/petty-cash': ModuleIds.PettyCash,
    '/leads': ModuleIds.Leads,
    '/customers': ModuleIds.Customers,
    '/quotation': ModuleIds.Quotation,
    '/invoice': ModuleIds.Invoice,
    '/sales-order': ModuleIds.SalesOrder,
    '/projects': ModuleIds.Project,
    '/refund': ModuleIds.Refund,
    '/payment-received': ModuleIds.PaymentReceived,
    '/order-flow-assigned-list': ModuleIds.OrderFlow,
    '/tasks': ModuleIds.TaskManagement,
    '/services': ModuleIds.ServiceManagement,
    '/vendors': ModuleIds.Vendor,
    '/vendor-invoice': ModuleIds.VendorInvoice,
    '/purchase-order': ModuleIds.PurchaseOrder,
    '/payments-made': ModuleIds.PaymentMade,
    '/stationary-items': ModuleIds.StationaryItems,
    '/stationary-log': ModuleIds.StationaryLog,
    '/daily-purchase': ModuleIds.DailyPurchase,
    '/suppliers': ModuleIds.Suppliers,
    '/reports': ModuleIds.Reports,
    '/notification-template':ModuleIds.NotificationTemplate
  };
  constructor(
    private router: Router,
    private permissionService: PermissionService
  ) {
    for (const route in this.routeModuleMap) {
      const moduleId = this.routeModuleMap[route];
      this.routePermissions[route] =
        this.permissionService.hasModulePermission(moduleId);
    }
  }

  hrmsRoutes = [
    '/employees',
    // '/salary-increment',
    '/attendance-management',
    '/leaves',
    // '/existing-vacation',
    '/payroll',
    '/holiday',
    '/vacation-management',
    '/designations',
    '/departments',
    // '/face-id-devices',
    // '/locations',
  ];

  accountRoutes = [
    '/currency-adjustments',
    '/manual-journals',
    '/chart-of-accounts',
  ];
  salesRoutes = [
    '/leads',
    '/customers',
    '/quotation',
    '/invoice',
    '/sales-order',
    '/order-flow-assigned-list',
    '/projects',
    '/refund',
    '/payment-received',
  ];

  serviceRoutes = ['/tasks', '/services'];
  purchaseRoute = [
    '/vendors',
    '/vendor-invoice',
    '/purchase-order',
    '/payments-made',
  ];
  assetsRoutes = [
    '/stationary-items',
    '/stationary-log',
    '/daily-purchase',
    '/suppliers',
  ];

  getRouteLabel(route: string): string {
    switch (route) {
      case '/employees':
        return 'Employees';
      case '/salary-increment':
        return 'Salary Increment';
      case '/attendance-management':
        return 'Attendance Management';
      case '/leaves':
        return 'Leaves';
      case '/existing-vacation':
        return 'Existing Vacation';
      case '/payroll':
        return 'Payroll';
      case '/holiday':
        return 'Holiday';
      case '/expenses':
        return 'Expenses';
      case '/vacation-management':
        return 'Vacation Management';
      case '/designations':
        return 'Designations';
      case '/departments':
        return 'Departments';
      case '/face-id-devices':
        return 'Face ID Devices';
      case '/locations':
        return 'Locations';
      case '/customers':
        return 'Customers';
      case '/vendors':
        return 'Vendors';
      case '/leads':
        return 'Leads';
      case '/quotation':
        return 'Quotation';
      case '/sales-order':
        return 'Sales Order';
      case '/order-flow-assigned-list':
        return 'Employee Order List';
      case '/projects':
        return 'Project';
      case '/invoice':
        return 'Invoice';
      case '/vendor-invoice':
        return 'Vendor Invoice';
      case '/tasks':
        return 'Task Management';
      case '/services':
        return 'Service Management';
      case '/refund':
        return 'Refund';
      case '/payments-made':
        return 'Payments Made';
      case '/payment-received':
        return 'Payments Received';
      case '/purchase-order':
        return 'Purchase Order';
      case '/stationary-log':
        return 'Stationary Log';
      case '/stationary-items':
        return 'Stationary Items';
      case '/daily-purchase':
        return 'Daily Purchase';
      case '/suppliers':
        return 'Suppliers';
      case '/currency-adjustments':
        return 'Currency Adjustments';
      case '/manual-journals':
        return 'Manual Journals';
      case '/chart-of-accounts':
        return 'Chart Of Accounts';
      default:
        return '';
    }
  }

  ngOnInit(): void {
    this.currentPath = this.router.url;
    const savedTab = localStorage.getItem('panelState');
    this.mattabnumber = savedTab ? JSON.parse(savedTab) : 0;
    if (this.allowedPath.includes(this.currentPath)) {
      this.mattabnumber = 0;
    }
    const loggedInUser: any = localStorage.getItem('loggedInUser');
    const userData = JSON.parse(loggedInUser);
    this.profileName = userData?.profileData?.profileName;
    // this.loggedInUser = userData;
    const storedData = localStorage.getItem('rolesAndPermision');
    if (storedData) {
      this.userModules = JSON.parse(storedData); // Array of modules with permissions
      console.log('this.userModules', this.userModules);
    }

    this.filteredHrmsRoutes = this.getVisibleHrmsRoutes(this.hrmsRoutes);
    this.filteredAccountsRoutes = this.getVisibleHrmsRoutes(this.accountRoutes);
    this.filteredSalesRoutes = this.getVisibleHrmsRoutes(this.salesRoutes);
    this.filteredPurchaseRoutes = this.getVisibleHrmsRoutes(this.purchaseRoute);
    this.filteredServiceRoutes = this.getVisibleHrmsRoutes(this.serviceRoutes);
    this.filteredAssetsRoutes = this.getVisibleHrmsRoutes(this.assetsRoutes);
  }

  getVisibleHrmsRoutes(routes: string[]): string[] {
    return routes.filter((route) => {
      const moduleId = this.routeModuleMap[route];
      if (!moduleId) return false;

      const moduleData = this.userModules.find((m) => +m.moduleId === moduleId);
      return (
        moduleData &&
        moduleData.permissions &&
        moduleData.permissions.length > 0
      );
    });
  }

  isModuleIdInUserModules(moduleId: number): boolean {
    const moduleData = this.userModules.find((m) => +m.moduleId === moduleId);
    return !!(
      moduleData &&
      moduleData.permissions &&
      moduleData.permissions.length > 0
    );
  }

  onPanelClick(): void {
    if (window.innerWidth > 768) {
      this.triggerSidebarAction.emit();
    }
  }

  toggleExpansionPanel(): void {
    this.expansionPanels.forEach((panel) => {
      if (panel.expanded) {
        panel.close();
      }
    });
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
    if (window.innerWidth < 993) {
      this.toggleSidebar();
    }
  }

  isRouteActive(route: string): boolean {
    return this.router.url.startsWith(route);
  }

  isRouteActive2(routes: string[]): boolean {
    return routes.some((path) => this.router.url.startsWith(path));
  }
}
